import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getRenews, deleteRenew } from "../../../Services/RenewTanentPlaneApi";
import { getPlane } from "../../../Services/plansApi"; // أضفنا جلب الباقات
import EditRenewDialog from "./EditRenewDialog"; // جديد: حوار تعديل التجديد

const AllSubscriptionsReportsTap = () => {
  const [renews, setRenews] = useState([]); // مجموعة السجلات كما هي من الـ API (مباشرة)
  const [selectedGroup, setSelectedGroup] = useState(null); // هنا سنخزن سجل تجديد واحد عند فتح الحوار
  const [openEdit, setOpenEdit] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deletingId, setDeletingId] = useState(null); // لمعالجة الحذف المؤقت

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRenews, setFilteredRenews] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(""); // فلتر اسم النسخة
  // فلتر الموظف وقائمة الأسماء
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [employeesList, setEmployeesList] = useState([]);
  // فلتر الباقة وقائمة الباقات
  const [planFilter, setPlanFilter] = useState("all");
  const [plansList, setPlansList] = useState([]);
  // فلتر الوسيط وقائمة الوسطاء
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [brokersList, setBrokersList] = useState([]);
  // فلتر تواريخ بداية ونهاية الباقة (من - إلى)
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [endFrom, setEndFrom] = useState("");
  const [endTo, setEndTo] = useState("");
  // فلتر الفرز: الحقل والاتجاه
  const [sortField, setSortField] = useState("renew_date"); // 'renew_date' | 'created_at' | 'updated_at' | 'none'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        setLoading(true);
        setError("");
        // نجلب الباقات وسجلات التجديد بالتوازي
        const [plansRes, renewsRes] = await Promise.all([getPlane(), getRenews()]);

        // بناء خريطة الباقات id -> planObject
        const plansArray = plansRes?.data?.data?.palnes || plansRes?.data?.palnes || [];
        const plansMap = {};
        Array.isArray(plansArray) && plansArray.forEach((p) => {
          if (p && p.id !== undefined) plansMap[String(p.id)] = p;
        });

        // استخراج مصفوفة التجديدات من الاستجابة
        const rawRenews = renewsRes?.data?.data ?? (Array.isArray(renewsRes?.data) ? renewsRes.data : []);
        // ضم اسم الباقة والأسعار لكل سجل
        const mapped = (Array.isArray(rawRenews) ? rawRenews : []).map((r) => {
          const planeId = r?.tenant?.plane_id;
          const planObj = plansMap[String(planeId)];
          return {
            ...r,
            plan_name: planObj?.plane_name ?? "غير محدد",
            plan_monthly: planObj?.monthly_price ?? null,
            plan_yearly: planObj?.yearly_price ?? null,
            plan_price: planObj?.price ?? null,
          };
        });
        if (mounted) {
          // خزّن السجلات كما هي للعرض التفصيلي
          setRenews(mapped);
          // استخرج أسماء الموظفين الفريدة للاختيار في الفلتر
          const emps = new Set();
          mapped.forEach((r) => {
            const name = (r.employee_name || r.employee || "").toString().trim();
            if (name) emps.add(name);
          });
          setEmployeesList(Array.from(emps).sort((a,b) => a.localeCompare(b)));
          // استخرج أسماء الباقات الفريدة
          const pls = new Set();
          mapped.forEach((r) => {
            if (r.plan_name) pls.add(r.plan_name.toString());
          });
          setPlansList(Array.from(pls).sort((a,b) => a.localeCompare(b)));
          // استخرج قائمة الوسطاء (نحاول استخلاص الاسم أو الإيميل أو الكود)
          const brks = new Set();
          mapped.forEach((r) => {
            const b = r.user?.broker;
            let bName = "";
            if (!b) return;
            if (typeof b === "string") bName = b;
            else bName = (b.name ?? b.email ?? b.code ?? "").toString();
            bName = bName.trim();
            if (bName) brks.add(bName);
          });
          setBrokersList(Array.from(brks).sort((a,b) => a.localeCompare(b)));
        }
      } catch (err) {
        console.error("getRenews error:", err);
        if (mounted) setError(err?.response?.data?.message || err.message || "حدث خطأ أثناء جلب البيانات");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, []);

  // فلترة السجلات حسب نص البحث (حقل واحد أو أكثر) + فلتر اسم النسخة
  useEffect(() => {
    const q = (searchTerm || "").toString().trim().toLowerCase();
    const tenantQ = (tenantFilter || "").toString().trim().toLowerCase();
    const empQ = (employeeFilter || "").toString().trim().toLowerCase();
    const planQ = (planFilter || "").toString().trim().toLowerCase();
    const brokerQ = (brokerFilter || "").toString().trim().toLowerCase();
    const sFrom = startFrom || "";
    const sTo = startTo || "";
    const eFrom = endFrom || "";
    const eTo = endTo || "";

    if (!q && !tenantQ && (!empQ || empQ === "all") && (!planQ || planQ === "all") && (!brokerQ || brokerQ === "all") && !sFrom && !sTo && !eFrom && !eTo) {
      setFilteredRenews(renews);
      return;
    }

    const filtered = renews.filter((r) => {
      // tenant id filter
      if (tenantQ) {
        const tenantId = (r.tenant?.id ?? r.tenant_id ?? "").toString().toLowerCase();
        if (!tenantId.includes(tenantQ)) return false;
      }
      // employee filter
      if (empQ && empQ !== "all") {
        const empName = (r.employee_name || r.employee || "").toString().toLowerCase();
        if (!empName.includes(empQ)) return false;
      }
      // plan filter
      if (planQ && planQ !== "all") {
        const pname = (r.plan_name || "").toString().toLowerCase();
        if (!pname.includes(planQ)) return false;
      }
      // broker filter
      if (brokerQ && brokerQ !== "all") {
        const b = r.user?.broker;
        let bName = "";
        if (b) {
          bName = typeof b === "string" ? b : (b.name ?? b.email ?? b.code ?? "");
        }
        if (!bName.toString().toLowerCase().includes(brokerQ)) return false;
      }

      // start-date range (بداية الباقة)
      if (sFrom || sTo) {
        const recStart = r.tenant?.start_date ?? r.start_date ?? null;
        const d = toDateOnly(recStart);
        const fromD = sFrom ? toDateOnly(sFrom) : null;
        const toD = sTo ? toDateOnly(sTo) : null;
        if (!d) return false;
        if (fromD && !toD) { if (d < fromD) return false; }
        else if (toD && !fromD) { if (d > toD) return false; }
        else if (fromD && toD) { if (d < fromD || d > toD) return false; }
      }
      // end-date range (نهاية الباقة)
      if (eFrom || eTo) {
        const recEnd = r.tenant?.end_date ?? r.end_date ?? null;
        const d = toDateOnly(recEnd);
        const fromD = eFrom ? toDateOnly(eFrom) : null;
        const toD = eTo ? toDateOnly(eTo) : null;
        if (!d) return false;
        if (fromD && !toD) { if (d < fromD) return false; }
        else if (toD && !fromD) { if (d > toD) return false; }
        else if (fromD && toD) { if (d < fromD || d > toD) return false; }
      }

      if (!q) return true;
      const parts = [
        r.user?.name,
        r.user?.email,
        r.tenant?.id,
        r.tenant_id,
        r.plan_name,
        r.employee_name,
        r.notes,
        r.cost?.toString?.(),
      ];
      return parts.some((p) => (p ?? "").toString().toLowerCase().includes(q));
    });
    // طبق الفرز إذا طُلب
    let final = filtered;
    if (sortField && sortField !== "none") {
      final = [...filtered].sort((x, y) => compareDates(x, y, sortField, sortOrder));
    }
    setFilteredRenews(final);
  }, [renews, searchTerm, tenantFilter, employeeFilter, planFilter, brokerFilter, startFrom, startTo, endFrom, endTo, sortField, sortOrder]);

  // helper: ترجمة حالة المستأجر إلى نص ولون
  const tenantStatusText = (status) => (status === 1 ? "نشط" : "غير نشط");
  const tenantStatusColor = (status) => (status === 1 ? "success" : "error");

  // مساعد لتهيئة التاريخ بصيغة عربية قصيرة مع وقت بنظام 12 ساعة
  const formatDate = (d) => {
    if (!d) return "-";
    try {
      const dt = new Date(d);
      const datePart = dt.toLocaleDateString("ar-EG");
      // نستخدم 'en-US' مع hour12:true لضمان عرض الوقت بنظام 12 ساعة (AM/PM)
      const timePart = dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
      return `${datePart} ${timePart}`;
    } catch {
      return d;
    }
  };
  
  // دالة مساعدة لتحويل نص التاريخ إلى Date بدون وقت (تدعم YYYY-MM-DD أو ISO)
  const toDateOnly = (s) => {
    if (!s) return null;
    try { return new Date(s.length === 10 ? `${s}T00:00:00` : s); } catch { return null; }
  };

  // دالة مقارنة لتواريخ (تدعم القيم الفارغة)
  const compareDates = (a, b, field, order = "desc") => {
    if (!field || field === "none") return 0;
    const da = toDateOnly(a?.[field] ?? a) ;
    const db = toDateOnly(b?.[field] ?? b) ;
    if (!da && !db) return 0;
    if (!da) return order === "asc" ? -1 : 1;
    if (!db) return order === "asc" ? 1 : -1;
    const diff = da - db;
    return order === "asc" ? diff : -diff;
  };

  // افتح حوار التعديل مع السجل
  const handleOpenEdit = (record) => {
    setEditRecord(record);
    setOpenEdit(true);
  };

  // عند نجاح التعديل - استبدل السجل المحدث في المصفوفة
  const handleEditSuccess = (updatedRecord, payload, id) => {
    // updatedRecord قد يكون في res.data.data أو res.data أو مباشرة
    const updated = updatedRecord?.data?.data ?? updatedRecord?.data ?? updatedRecord;
    const updatedId = id ?? updated?.id ?? updated?.renew_id ?? updated?.renew?.id;
    setRenews((prev) => prev.map((r) => (String(r.id) === String(updatedId) ? { ...r, ...updated } : r)));
    setOpenEdit(false);
    setEditRecord(null);
  };

  // حذف سجل التجديد (يطلب تأكيد ثم يستدعي API ويحذف محلياً)
  const handleDelete = async (record) => {
    const id = record?.id;
    if (!id) return;
    const ok = window.confirm(`هل أنت متأكد من حذف التجديد #${id}؟`);
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteRenew(id);
      // إزالة السجل من الواجهة محلياً
      setRenews((prev) => prev.filter((r) => String(r.id) !== String(id)));
    } catch (err) {
      console.error("deleteRenew error:", err);
      setError(err?.response?.data?.message || err.message || "فشل حذف التجديد");
    } finally {
      setDeletingId(null);
    }
  };

  // بعد تعيين filteredRenews داخل useEffect أو بعده أضف الدوال التالية
  // (ضعها ضمن نفس المكوّن، قبل return)
  const escapeCsv = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const buildCsvContent = (rows) => {
    // رؤوس بالعربية
    const headers = [
      "معرف التجديد",
      "معرف المستأجر",
      "اسم المستخدم",
      "البريد الإلكتروني",
      "الوسيط",
      "اسم الباقة",
      "السعر الشهري",
      "تاريخ بداية الباقة",
      "تاريخ نهاية الباقة",
      "حالة الاشتراك",
      "التكلفة",
      "تاريخ التجديد",
      "الموظف",
      "ملاحظات",
      "تاريخ الإنشاء",
      "تاريخ التعديل",
    ];
    const lines = [headers.map(escapeCsv).join(",")];

    rows.forEach((r) => {
      const broker = r.user?.broker;
      const brokerText = broker ? (typeof broker === "string" ? broker : broker.name ?? broker.email ?? broker.code ?? "") : "";
      const statusText = (r.tenant?.status === 1 ? "نشط" : (r.tenant?.status === 0 ? "غير نشط" : ""));
      const row = [
        r.id ?? "",
        r.tenant?.id ?? r.tenant_id ?? "",
        r.user?.name ?? "",
        r.user?.email ?? "",
        brokerText,
        r.plan_name ?? "",
        r.plan_monthly != null ? r.plan_monthly : "",
        r.tenant?.start_date ?? r.start_date ?? "",
        r.tenant?.end_date ?? r.end_date ?? "",
        statusText,
        r.cost ?? "",
        r.renew_date ?? r.created_at ?? "",
        r.employee_name ?? "",
        r.notes ?? "",
        r.created_at ?? "",
        r.updated_at ?? "",
      ];
      lines.push(row.map(escapeCsv).join(","));
    });
    return lines.join("\n");
  };

  const downloadCsv = (content, filename = "renews_export.csv") => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    try {
      const csv = buildCsvContent(filteredRenews || []);
      const now = new Date();
      const name = `renews_export_${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}.csv`;
      downloadCsv(csv, name);
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  return (
    <Box dir="rtl" sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)" }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 800, color: "#fff", textAlign: "center", textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}>
        📊 تقارير التجديدات الشاملة
      </Typography>

      {/* بطاقة الفلاتر */}
      <Paper elevation={8} sx={{ p: 3, mb: 3, borderRadius: 3, background: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <FilterListIcon sx={{ color: "#ff6a00", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c3e50" }}>
            🔍 البحث والفلترة المتقدمة
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* صف أول: البحث والنسخة */}
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="ابحث باسم المستخدم، البريد، الباقة، الموظف، الملاحظات..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: "1 1 350px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            size="small"
            placeholder="فلتر باسم/معرف النسخة"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          />
        </Box>

        {/* صف ثاني: فلاتر الموظف والباقة والوسيط */}
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="employee-filter-label">👤 الموظف</InputLabel>
            <Select
              labelId="employee-filter-label"
              value={employeeFilter}
              label="👤 الموظف"
              onChange={(e) => setEmployeeFilter(e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              {employeesList.map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="plan-filter-label">📦 الباقة</InputLabel>
            <Select
              labelId="plan-filter-label"
              value={planFilter}
              label="📦 الباقة"
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              {plansList.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="broker-filter-label">🤝 الوسيط</InputLabel>
            <Select
              labelId="broker-filter-label"
              value={brokerFilter}
              label="🤝 الوسيط"
              onChange={(e) => setBrokerFilter(e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              {brokersList.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* صف ثالث: فلاتر التواريخ */}
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Paper elevation={2} sx={{ p: 1.5, display: "flex", gap: 1, alignItems: "center", borderRadius: 2, background: "#f8f9fa" }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>📅 بداية الباقة:</Typography>
            <TextField label="من" type="date" size="small" value={startFrom} onChange={(e)=>setStartFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <TextField label="إلى" type="date" size="small" value={startTo} onChange={(e)=>setStartTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          </Paper>
          <Paper elevation={2} sx={{ p: 1.5, display: "flex", gap: 1, alignItems: "center", borderRadius: 2, background: "#f8f9fa" }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>📅 نهاية الباقة:</Typography>
            <TextField label="من" type="date" size="small" value={endFrom} onChange={(e)=>setEndFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <TextField label="إلى" type="date" size="small" value={endTo} onChange={(e)=>setEndTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          </Paper>
        </Box>

        {/* صف رابع: الفرز والإجراءات */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="sort-field-label">🔄 ترتيب حسب</InputLabel>
              <Select
                labelId="sort-field-label"
                value={sortField}
                label="🔄 ترتيب حسب"
                onChange={(e) => setSortField(e.target.value)}
              >
                <MenuItem value="none">بلا ترتيب</MenuItem>
                <MenuItem value="renew_date">تاريخ التجديد</MenuItem>
                <MenuItem value="created_at">تاريخ الإنشاء</MenuItem>
                <MenuItem value="updated_at">تاريخ التعديل</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="sort-order-label">⬆️ الاتجاه</InputLabel>
              <Select
                labelId="sort-order-label"
                value={sortOrder}
                label="⬆️ الاتجاه"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="desc">تنازلي ⬇️</MenuItem>
                <MenuItem value="asc">تصاعدي ⬆️</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button 
              variant="outlined" 
              color="error"
              size="medium"
              startIcon={<ClearIcon />}
              onClick={() => {
                setSearchTerm(""); setTenantFilter(""); setEmployeeFilter("all"); setPlanFilter("all"); setBrokerFilter("all");
                setStartFrom(""); setStartTo(""); setEndFrom(""); setEndTo("");
                setSortField("renew_date"); setSortOrder("desc");
              }}
              sx={{ textTransform: "none", fontWeight: 600, px: 3 }}
            >
              مسح الكل
            </Button>
            <Button 
              variant="contained" 
              color="success"
              size="medium"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ textTransform: "none", fontWeight: 600, px: 3 }}
            >
              تصدير Excel
            </Button>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper elevation={4} sx={{ p: 3, background: "#ffebee", borderRadius: 3 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <Paper elevation={8} sx={{ p: 2.5, borderRadius: 3, background: "#fff" }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c3e50" }}>
              📊 النتائج ({filteredRenews.length} سجل)
            </Typography>
          </Box>
          <TableContainer dir="rtl" sx={{ maxHeight: 520, borderRadius: 2, border: "1px solid #e0e0e0" }}>
            <Table stickyHeader size="small" sx={{ "& .MuiTableCell-head": { background: "linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)", color: "#fff", fontWeight: 700, fontSize: "0.9rem" } }}>
              <TableHead>
                <TableRow>
                  <TableCell align="right">المستخدم</TableCell>
                  <TableCell align="right">اسم النسخة</TableCell>
                  <TableCell align="right">الوسيط</TableCell>
                  <TableCell align="right">الباقة</TableCell>
                  <TableCell align="right">بداية الباقة</TableCell>
                  <TableCell align="right">نهاية الباقة</TableCell>
                  <TableCell align="right">التكلفة</TableCell>
                  <TableCell align="right">تاريخ التجديد</TableCell>
                  <TableCell align="right">الموظف</TableCell>
                  <TableCell align="right">ملاحظات</TableCell>
                  <TableCell align="right">تاريخ الإنشاء</TableCell>
                  <TableCell align="right">تاريخ التعديل</TableCell>
                  <TableCell align="right">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableRow-root:hover": { background: "#f5f7fa", transition: "all 0.2s" } }}>
                {filteredRenews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      لا توجد سجلات
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRenews.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700 }}>{r.user?.name ?? "-"}</Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{r.user?.email ?? "-"}</Typography>
                      </TableCell>
                      <TableCell align="right">{r.tenant?.id ?? r.tenant_id ?? "-"}</TableCell>
                      <TableCell align="right">{r.user?.broker?.name ?? "-"}</TableCell>
                      <TableCell align="right">{r.plan_name ?? "-"}</TableCell>
                      <TableCell align="right">{r.tenant?.start_date ?? "-"}</TableCell>
                      <TableCell align="right">{r.tenant?.end_date ?? "-"}</TableCell>
                      <TableCell align="right">{r.cost ? `${r.cost} ج` : "-"}</TableCell>
                      <TableCell align="right">{r.renew_date ?? "-"}</TableCell>
                      <TableCell align="right">{r.employee_name ?? "-"}</TableCell>
                      <TableCell align="right" title={r.notes ?? ""} sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.notes ?? "-"}
                      </TableCell>
                      <TableCell align="right">{formatDate(r.created_at)}</TableCell>
                      <TableCell align="right">{formatDate(r.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(r)} title="تعديل">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(r)}
                            title="حذف"
                            disabled={deletingId === r.id}
                          >
                            {deletingId === r.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit dialog */}
      <EditRenewDialog
        open={openEdit}
        onClose={() => { setOpenEdit(false); setEditRecord(null); }}
        renew={editRecord}
        onSuccess={handleEditSuccess}
      />

    </Box>
  );
};

export default AllSubscriptionsReportsTap;
